'use client';

import { jsx as _jsx } from "react/jsx-runtime";
import { createLogger } from '@svenskaspelab/nova-core/log';
import { useEffect, useMemo, useRef, useState } from 'react';
import { widgetIds } from '../../providers/enetpulse/entry.js';
import { ErrorFallback } from '../ErrorFallback.js';
const logger = createLogger('poolbetting.sportinfo.enetpulse');
/**
 * EnetpulseWidget component to load Enetpulse widgets dynamically.
 *
 * @param props - The properties for the Enetpulse widget.
 * @param props.item - The sportinfo content object containing widget details.
 * @param props.eventProps - The event properties including eventId, drawNumber, and providers.
 * @returns The rendered Enetpulse widget or fallback component.
 */
export function EnetpulseWidget({
  item,
  eventProps
}) {
  const {
    viewProps,
    type
  } = item;
  const {
    providers
  } = eventProps;
  const options = useMemo(() => viewProps?.options ?? {}, [viewProps]);
  const hasMultipleWidgets = Boolean(viewProps?.hasMultipleWidgets);
  const kambiProvider = providers?.Kambi;
  const hasKambiProvider = Boolean(kambiProvider);
  const containerRef = useRef(null);
  const [hasWidgetLoadError, setHasWidgetLoadError] = useState(false);
  const widgetId = widgetIds[type];
  const shouldShowFallbackComp = !hasKambiProvider || hasWidgetLoadError;
  useEffect(() => {
    if (!hasKambiProvider || !containerRef.current) {
      return;
    }
    const script = document.createElement('script');
    script.id = 'enetpulse-widget';
    script.defer = true;
    let enetpulseIdInitialiser = 'ev';
    if (widgetId === widgetIds.TournamentStandings) {
      enetpulseIdInitialiser = 'tsid';
    } else if (widgetId === widgetIds.DrawStandings) {
      enetpulseIdInitialiser = 'drid';
    }
    let src = `https://widget.enetscores.com/${widgetId}/${enetpulseIdInitialiser}/${kambiProvider}`;
    const optionKeys = Object.keys(options);
    if (optionKeys.length > 0) {
      for (const key of optionKeys) {
        src += `/${key}/${options[key]}`;
      }
    }
    script.src = src;
    const element = containerRef.current;
    const handleError = () => {
      const {
        eventId,
        drawNumber
      } = eventProps;
      logger.warn('Failed to load Enetpulse widget', {
        widgetId,
        eventId: kambiProvider,
        eventNumber: eventId,
        drawNumber: drawNumber
      });
      setHasWidgetLoadError(true);
    };
    script.addEventListener('error', handleError);
    element.append(script);
    const cleanup = () => {
      script.removeEventListener('error', handleError);
      if (element.contains(script)) {
        script.remove();
        element.innerHTML = '';
      }
    };
    // eslint-disable-next-line consistent-return
    return cleanup;
  }, [eventProps, hasKambiProvider, kambiProvider, options, widgetId]);
  if (shouldShowFallbackComp && !hasMultipleWidgets) {
    return _jsx(ErrorFallback, {});
  }
  return _jsx("div", {
    id: "enetpulse-container",
    ref: containerRef
  });
}